import styles from './sidepane.module.scss';
import { useAuth0 } from '@auth0/auth0-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import { store } from 'stores/Store';
import { observer } from 'mobx-react';
import Select from 'react-select';
import { voiceModeLocalStorage } from 'stores/Localstorage';

import useExternalApiService from 'helpers/ExternalApiService';

import { init, setUserId, logEvent } from '@amplitude/analytics-browser'; // ← browser SDK
import { Center, Container, Loader, Stack } from '@mantine/core';

interface SidepaneContentProps {
  togglePane: () => void;
}

const SidepaneContent: React.FC<SidepaneContentProps> = observer(({ togglePane }) => {

  const { logout } = useAuth0();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);

  const [currentVoiceMode, setCurrentVoiceMode] = React.useState(voiceModeLocalStorage.get() ?? { value: 'TEXT', label: 'Text Response' });

  // Helper to track all button clicks with a consistent event
  const trackClick = (buttonName: string) => {
    const label = 'Sidepane Button: ' + buttonName
    logEvent(label, { buttonName });
  };

  const isImpersonateListLoading = !(!!store?.user?.impersonateList);

  //console.log("sidepane code userName: ", store.user?.userName);

  const downloadCsv = async () => {
    trackClick('Export Check-In CSV');
    try {
      // Calls the POST endpoint and expects the CSV text in the response
      //const { csv } = await postData('/api/export_checkins', {"fmc_email": store.impersonatedUser}); // This calls the main service
      const { csv } = await postData('/api/e/winback_export_checkins', { "fmc_email": store.impersonatedUser });  // This calls the microservice so we have easier access to user_impersonate.csv
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${store.impersonatedUser?.split('@')[0]}_checkins_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Sorry, could not download the report.');
    }
  };

  const doLogout = () => {
    trackClick('Log Out');
    logout({ logoutParams: { returnTo: window.location.origin } })
  }


  React.useEffect(() => {
    voiceModeLocalStorage.save(currentVoiceMode ?? 'TEXT');
  }, [currentVoiceMode]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.row}>
        {store.user?.userPicture ? (
          <img
            className={styles.userPicture}
            src={store.user.userPicture}
          />
        ) :
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#E95540',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              minWidth: '48px',
              minHeight: '48px',
              flexShrink: 0
            }}
          >
            {store.user?.userFriendlyName?.charAt(0) || ''}
          </div>
        }
        <div className={styles.column}>
          <Text type='header'>{store.user?.userFriendlyName || ''}</Text>
          <Text type='smaller'>{store.user?.userName || ''}</Text>
        </div>
      </div>
      <div className={styles.listWrapper}>
        {store.user && Array.isArray(store.user.impersonateList) && store.user.impersonateList.length > 0 && (
          <>
            <Text type='primary'>View as User</Text>
            <Select
              isLoading={isImpersonateListLoading}
              isDisabled={isImpersonateListLoading}
              value={store.impersonatedUser ? { value: store.impersonatedUser, label: store.impersonatedUser } : null}
              onChange={(option: any) => store.setImpersonatedUser(option?.value || '')}
              options={[
                { value: store.user.userName, label: store.user.userName },
                ...store.user.impersonateList.map((fmc) => ({
                  value: fmc,
                  label: fmc
                }))
              ]}
              isClearable
              placeholder="Select a user..."
            />
          </>
        )}
      </div>
      <div className={styles.listWrapper}>
        <Text type='primary'>Quick-Links</Text>
        {isImpersonateListLoading ? (
          <Container>
            <Stack>
              <Center>
                <Loader />
              </Center>
              <Center>
                <p style={{ textAlign: "center"}}>
                  AI is being updated, please reload in a few minutes
                </p>
              </Center>
            </Stack>
          </Container>
        ) : (
          <>
            {(!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && (
              <Button type='transparent' onClick={() => {
                trackClick('Hero SKU Heatmap');
                togglePane();
                navigate(`/?${new URLSearchParams({ prompt: 'Show me my hero sku heatmap' }).toString()}`)
              }
              }>
                Top Products Heatmap
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('Customer List');
                togglePane();
                navigate(`/?${new URLSearchParams({ prompt: 'practitioners' }).toString()}`)
              }
              }>
                Customer List
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('My Routes');
                togglePane();
                navigate(`/?${new URLSearchParams({ prompt: 'create a new route' }).toString()}`)
              }
              }>
                My Routes
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('Territory Map');
                togglePane();
                navigate(`/mapView`)
              }
              }>
                Territory Map
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('Create Lead');
                togglePane();
                navigate(`/?${new URLSearchParams({ prompt: 'help me create a new lead' }).toString()}`)
              }
              }>
                Create Lead
              </Button>
            )}
            {store.user?.earlyRelease && (
              <Button type='transparent' onClick={() => {
                togglePane();
                downloadCsv();
              }
              }>
                Export Check-In CSV
              </Button>
            )}
          </>
        )}
      </div>
      <div className={styles.listWrapper}>
        <>
          <Text type='primary'>Voice Mode</Text>
          <Select
            options={[
              { value: 'TEXT', label: 'Text Response' },
              { value: 'VOICE', label: 'Voice Response' }
            ]}
            value={currentVoiceMode}
            onChange={(option: any) => {
              setCurrentVoiceMode(option)
            }}
          />
        </>
      </div>

      <div className={styles.divider} />
      <div className={styles.listWrapper}>
        <Button
          onClick={doLogout}
          type='transparent'>Log out</Button>
      </div>
    </div>
  )
})

export default SidepaneContent;